import React from "react";
import { HiOutlineClock } from "react-icons/hi";
import get from "lodash/get";
import classNames from "classnames";

// components
import AgePieChart from "../AgePieChart";
import WeeklyFootfall from "../WeeklyFootfall";
import StatisticCard from "@/components/StatisticCard";
import PieChartIcon from "@/components/icons/PieChartIcon"
import FemaleIcon from "@/components/icons/FemaleIcon";
import MaleIcon from "@/components/icons/MaleIcon";
import FootfallIcon from "@/components/icons/FootfallIcon";

// helpers
import currencyUtils from "@/helpers/currencyUtils";
import useCurrency from "@/hooks/useCurrency";
import getCurrencyIcon from "@/helpers/getCurrencyIcon";
import separatedNumber from "@/helpers/separatedNumber";

// interfaces
import { ILocationSpaceStatisticsContainer } from "./ILocationSpaceStatisticsContainer";

// assets
import styles from "@/assets/styles/containers/location-space-statistics-container.module.scss";


function LocationSpaceStatisticsContainer(props: ILocationSpaceStatisticsContainer) {
  const {
    demographic,
    defaultCurrency,
    isPreview,
    isMobileView,
    isTabletView,
    isCollapsing,
  } = props;

  const [selectedCurrency] = useCurrency();
  const currencyToUse = isPreview ? defaultCurrency : selectedCurrency || defaultCurrency;

  const dailyFootfall = [
    { label: "Mon", value: get(demographic, "dailyFootfall.monday") },
    { label: "Tue", value: get(demographic, "dailyFootfall.tuesday") },
    { label: "Wed", value: get(demographic, "dailyFootfall.wednesday") },
    { label: "Thu", value: get(demographic, "dailyFootfall.thursday") },
    { label: "Fri", value: get(demographic, "dailyFootfall.friday") },
    { label: "Sat", value: get(demographic, "dailyFootfall.saturday") },
    { label: "Sun", value: get(demographic, "dailyFootfall.sunday") },
  ].filter(item => !isNaN(+(item.value as number)));

  const ageChartData = [
    { label: "(Z)  16-24 yrs", value: get(demographic, "age.genZ"), color: "var(--dark-gold-color-500)" },
    { label: "(Y)  25-39 yrs", value: get(demographic, "age.genY"), color: "var(--purple-color-500)" },
    { label: "(X)  40-58 yrs", value: get(demographic, "age.genX"), color: "var(--danger-color-500)" },
    { label: "(BB) 59-68 yrs", value: get(demographic, "age.babyBoomer"), color: "var(--primary-color-500)" },
  ].filter(item => !!item.value);

  const shouldRenderFootfallChart = !!dailyFootfall.length && dailyFootfall.some(item => !!item.value);
  const CurrencyIcon = getCurrencyIcon(currencyToUse);

  const renderCardsList = () => (
    <div className={styles["locl-location-space-statistics-container__cards-list"]}>
      {!!demographic.dwellTime && (
        <StatisticCard
          data-testid="locl-location-space-statistics-container-card-time"
          className={styles["locl-location-space-statistics-container__card__time"]}
          icon={<HiOutlineClock />}
          label="Avg. dwell time/visit"
          value={`${demographic.dwellTime} mins`}
        />
      )}
      {!!demographic.abc1 && (
        <StatisticCard
          data-testid="locl-location-space-statistics-container-card-abc"
          className={styles["locl-location-space-statistics-container__card__abc"]}
          icon={<PieChartIcon />}
          label="% ABC1"
          value={`${demographic.abc1}%`}
          tooltipText="ABC1 % represents the higher socio-economic classes, including professionals, managers, and higher-income individuals."
        />
      )}
      {typeof demographic.spending !== "undefined" && (
        <StatisticCard
          data-testid="locl-location-space-statistics-container-card-spend"
          className={styles["locl-location-space-statistics-container__card__spend"]}
          icon={<CurrencyIcon />}
          label="Avg. spend/visit"
          value={currencyUtils.formatCurrency(demographic.spending[currencyToUse], currencyToUse)}
        />
      )}
      {!!demographic.gender.female && (
        <StatisticCard
          data-testid="locl-location-space-statistics-container-card-women"
          className={styles["locl-location-space-statistics-container__card__women"]}
          icon={<FemaleIcon />}
          label="Women"
          value={`${demographic.gender.female}%`}
        />
      )}
      {!!demographic.gender.male && (
        <StatisticCard
          data-testid="locl-location-space-statistics-container-card-men"
          className={styles["locl-location-space-statistics-container__card__men"]}
          icon={<MaleIcon />}
          label="Men"
          value={`${demographic.gender.male}%`}
        />
      )}
      {(!shouldRenderFootfallChart && !!demographic.weeklyFootfall) && (
        <StatisticCard
          data-testid="locl-location-space-statistics-container-card-footfall"
          className={styles["locl-location-space-statistics-container__card__footfall"]}
          icon={<FootfallIcon />}
          label="Weekly footfall"
          value={`${separatedNumber(demographic.weeklyFootfall) || null}`}
        />
      )}
    </div>
  );

  return (
    <div className={classNames(
      styles["locl-location-space-statistics-container"],
      { [styles["locl-location-space-statistics-container__view-mobile"]]: isMobileView },
    )}>
      {shouldRenderFootfallChart && renderCardsList()}
      <div className={styles["locl-location-space-statistics-container__charts"]}>
        {shouldRenderFootfallChart ? (
          <WeeklyFootfall
            dataSource={dailyFootfall as Array<{ label: string; value: number }>}
            totalFootFall={demographic.weeklyFootfall ? Math.round(demographic.weeklyFootfall) : 0}
            isTabletView={isTabletView}
            isCollapsing={isCollapsing}
          />
        ) : renderCardsList()}
        {!!ageChartData.length && (
          <AgePieChart
            dataSource={ageChartData as Array<{ label: string; value: number, color: string }>}
            isMobileView={!!isMobileView}
            isCollapsing={isCollapsing}
          />
        )}
      </div>
      <div className={styles["locl-location-space-statistics-container__provider"]}>
        {demographic?.provider ? (
          <p data-testid="locl-location-space-statistics-container-provider-text">{demographic?.provider}</p>
        ) : (
          <>
            <p data-testid="locl-location-space-statistics-container-provider-default-text">
              This data was provided by the location provider. <br />
              Data is common to all spaces within the location. Please contact{" "}
              <a href={`mailto:${process.env.NEXT_PUBLIC_CREW_EMAIL}`}>{process.env.NEXT_PUBLIC_CREW_EMAIL}</a> for more information.
            </p>
          </>
        )}
      </div>
    </div>
  );
}


export default LocationSpaceStatisticsContainer;
